import React from 'react'

import moment from 'moment'
import PropTypes from 'prop-types'

import OrderSummary from 'containers/welcome/OrderSummary'
import { capitalizeFirstLetter } from 'utils/text'

import { AlignItems, Box, Color, Display, FontWeight, JustifyContent, Space, Text, TextAlign } from '@gousto-internal/citrus-react'
import { useGetDiscountSofteningFreeDelivery } from 'hooks/useGetDiscountSofteningFreeDelivery'
import { useAuth } from 'routes/Menu/domains/auth'
import { EventDate } from './EventDate'

import css from './OrderSchedule.css'

const propTypes = {
  deliveryDate: PropTypes.string.isRequired,
  nameFirst: PropTypes.string,
  whenCutoff: PropTypes.string.isRequired,
  interval: PropTypes.string,
  numRecipes: PropTypes.number.isRequired,
  numPortions: PropTypes.number.isRequired,
}

const defaultProps = {
  nameFirst: '',
  interval: 'weekly',
}

const isValidDate = (date) => (date && date.length) || typeof date === 'object'

export const getTopValue = () => {
  const width = window.innerWidth
  if (width <= 400) {
    return '0px'
  } else {
    return '-50px'
  }
}

const OrderSchedule = ({
  deliveryDate,
  nameFirst,
  whenCutoff,
  interval,
  numRecipes,
  numPortions,
}) => {
  const isValidDeliveryDate = isValidDate(deliveryDate)
  const isValidWhenCutoff = isValidDate(deliveryDate)

  const isFiveForFourOrder = numRecipes === 5 && numPortions === 4

  const { isAuthenticated } = useAuth()

  const isDiscountSofteningFreeDeliveryEnabled = useGetDiscountSofteningFreeDelivery(isAuthenticated)[0]

  return (
    <div data-testing="orderScheduleContainer" className={css.container}>
      <div className={css.header}>
        <h2>{`Congrats on choosing your first recipes, ${capitalizeFirstLetter(nameFirst)}!`}</h2>
        <p className={css.headerSpacing}>
          Don&apos;t forget our menu changes every Tuesday - there will be new recipes for you to
          try every week.
        </p>
        <h2>What happens next?</h2>
      </div>
      <div className={css.scheduleItem}>
        <div>
          <EventDate date={isValidWhenCutoff && deliveryDate} />
          <div className={css.verticalLine} />
        </div>
        <div className={css.information}>
          <h3 className="first-order-header">Your 1st order</h3>
          <p className="first-order-text">
            Changes can be made up until{' '}
            <b>
              {isValidWhenCutoff ? moment(whenCutoff, 'YYYY-MM-DD').format('dddd Do MMMM') : ''}
            </b>
            {isFiveForFourOrder
              ? ". Heads up, your order could arrive in 1 or 2 boxes. We'll let you know when it's packed."
              : ''}
          </p>
          <div className={css.mobileShow}>
            <OrderSummary />
          </div>
        </div>
      </div>
      <div className={css.scheduleItem}>
        <div>
          <EventDate
            date={isValidDeliveryDate && moment(deliveryDate, 'YYYY-MM-DD').add(4, 'days')}
          />
          <div className={css.verticalLine} />
        </div>
        <div className={css.information}>
          <h3 className="second-order-header">Your 2nd order cut-off</h3>
          {isDiscountSofteningFreeDeliveryEnabled && (
            <>
              <div data-testid="discount_tag" style={{display: 'flex'}}>
                <Box
                  display={Display.Flex}
                  alignItems={AlignItems.Center}
                  justifyContent={JustifyContent.Center}
                  width="56px"
                  height="20px"
                  bg={Color.Success_800}
                  borderRadius={1}
                >
                  <Text
                    size={0}
                    fontWeight={FontWeight.Bold}
                    textAlign={TextAlign.Center}
                    color={Color.White}
                  >
                    40% off
                  </Text>
                </Box>
                <Space size={1} direction="horizontal" />
                <Box>
                  <Text size={2} fontWeight={FontWeight.Bold} color={Color.Success_800}>on this box*</Text>
                </Box>
              </div>
              <Space size={1} />
            </>
          )}
          <p>
            Pick your recipes
            <b> before 12pm </b>
            on this date (don’t worry we&apos;ll remind you).
            <b> If you miss this we&apos;ll choose for you.</b>
          </p>
        </div>
      </div>
      <div className={css.scheduleItem}>
        <div>
          <EventDate
            date={isValidDeliveryDate && moment(deliveryDate, 'YYYY-MM-DD').add(7, 'days')}
          />
        </div>
        <div className={css.information}>
          <h3 className="second-order-delivery-header">Delivery of 2nd order</h3>
          <p className="second-order-delivery-text">
            Your
            <b>{` ${interval} `}</b>
            Gousto delivery will arrive every
            {` ${
              isValidDeliveryDate ? moment(deliveryDate, 'YYYY-MM-DD').format('dddd') : ''
            }. You'll get tracking information on the day.`}
          </p>
        </div>
      </div>
      {isDiscountSofteningFreeDeliveryEnabled && (
        <>
          <div data-testid="discount_tag_asterisk_text" style={{position: 'relative', top: getTopValue() }}>
            <Text size={0} color={Color.ColdGrey_800}>*Offer expires 2 months after sign up</Text>
          </div>
        </>
      )}
    </div>
  )
}

OrderSchedule.propTypes = propTypes
OrderSchedule.defaultProps = defaultProps

export { OrderSchedule }
